import { OpenInNew } from '@mui/icons-material'
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'
import { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { ActivityEntityType, ActivityTrackingAction } from '../../../generated'
import { formatDateTime } from '../../utils'
import { ACTION_LABELS, formatActivityDescription, getActivityType } from './activityMeta'

interface ActivityItemProps {
  action: ActivityTrackingAction
  description?: string | null
  entityType?: ActivityEntityType | null
  billId?: number | null
  hideMatterReference?: boolean
  userName: string
  createdAt: string
  isLast: boolean
}

/** Turns the bill's "#123" in the description into a link that opens the bill in a new tab. */
const withBillLink = (text: string, billId?: number | null): ReactNode => {
  const token = `#${billId}`
  const at = billId ? text.indexOf(token) : -1
  if (at < 0) return text

  return (
    <>
      {text.slice(0, at)}
      <Link
        component={RouterLink}
        to={`/billing/bill-detail/${billId}`}
        target="_blank"
        rel="noopener"
        underline="hover"
        color="primary"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
      >
        {token}
        <OpenInNew sx={{ fontSize: 12 }} />
      </Link>
      {text.slice(at + token.length)}
    </>
  )
}

export const ActivityItem = ({
  action,
  description,
  entityType,
  billId,
  hideMatterReference,
  userName,
  createdAt,
  isLast
}: ActivityItemProps) => {
  const { Icon, label, tag } = getActivityType(entityType)
  const details = formatActivityDescription(description, entityType, hideMatterReference)

  return (
    <Box component="li" display="flex" gap={2} sx={{ listStyle: 'none' }}>
      {/* Icon column, with the line that runs down to the next activity. */}
      <Box display="flex" flexDirection="column" alignItems="center">
        <Tooltip title={label} placement="left">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            width={36}
            height={36}
            borderRadius="50%"
            sx={{ color: tag.color, backgroundColor: alpha(tag.color, 0.1) }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
        {!isLast && <Box flex={1} width="1px" my={0.5} bgcolor="lightGrayTwo" />}
      </Box>

      <Box flex={1} minWidth={0} pb={isLast ? 0 : 2.5}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontSize={14} color="gray900">
            {ACTION_LABELS[action]}
          </Typography>
          <Chip
            label={tag.label}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              color: tag.color,
              backgroundColor: alpha(tag.color, 0.08),
              border: `1px solid ${alpha(tag.color, 0.24)}`
            }}
          />
        </Stack>

        {details && (
          <Typography fontSize={14} color="lightGrayFive" mt={0.25} sx={{ overflowWrap: 'anywhere' }}>
            {withBillLink(details, billId)}
          </Typography>
        )}

        <Typography fontSize={12} color="lightGrayFive" mt={0.5}>
          {userName} ·{' '}
          <Tooltip title={formatDateTime(createdAt)} placement="top">
            <span>{dayjs(createdAt).format('h:mm A')}</span>
          </Tooltip>
        </Typography>
      </Box>
    </Box>
  )
}
